import { z } from "zod";

// Mirrors the DB CHECK on skill.code; topic codes follow the same shape for
// consistency even though the DB does not constrain them.
const CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;

// The two schema families below validate the same rules over different wire
// formats, so the messages live here and are never written twice.
const CODE_REQUIRED_MESSAGE = "Code is required.";
const CODE_SHAPE_MESSAGE =
  "Use UPPER_SNAKE, starting with a letter (e.g. READING).";
const LABEL_REQUIRED_MESSAGE = "Label is required.";
// A skill is the whole progression display, so "Reading" with nothing saying
// what counts toward it is the axis label missing from the chart. The database
// agrees: skill_translation.description is NOT NULL.
const DESCRIPTION_REQUIRED_MESSAGE = "Description is required.";
const COLOR_MESSAGE = "Use a hex color such as #2563EB.";
const SORT_ORDER_MESSAGE = "Sort order must be a whole number.";
const KIND_MESSAGE = "Pick one of the listed kinds.";

// Mirrors the DB CHECK on tag.kind; a select over these values keeps the
// constraint unbreakable from the UI.
export const TAG_KINDS = ["FREE", "EQUIPMENT", "COST", "SEASON"] as const;

// The closed classification sets, each mirroring a DB CHECK. Topics are rows
// because an author can type a new one and nothing breaks; these cannot be
// typed in, because each needs application code that knows the value.
export const GOAL_TYPES = ["THRESHOLD", "MANUAL"] as const;

// Authoring originality, not user-facing intensity. Binary on purpose: a
// middle tier gets graded inconsistently by every author who meets it.
export const CONTENT_GRADES = ["STOCK", "CRAFTED"] as const;
export const VERIFICATIONS = [
  "AUTO",
  "SELF_REPORT",
  "PHOTO",
  "GPS_TRACE",
] as const;
export const PARTICIPATIONS = ["SOLO", "PAIR", "COUPLE", "GROUP"] as const;
export const SETTINGS = ["INDOOR", "OUTDOOR", "EITHER"] as const;
export const EFFORTS = ["QUICK", "SHORT", "MEDIUM", "LONG"] as const;

// Mirrors the DB CHECK on tag.slug. Templates hold tags as plain strings with
// no FK, so an undisciplined one is only caught here.
const TAG_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const TAG_SLUG_MESSAGE =
  "Use lower-case words joined by single hyphens (e.g. no-equipment).";

// Form schemas: every field arrives from FormData as a string, so these coerce
// on the way in. Server Actions and `parseFormData` use these.

const codeField = z
  .string()
  .trim()
  .min(1, CODE_REQUIRED_MESSAGE)
  .regex(CODE_PATTERN, CODE_SHAPE_MESSAGE);

const labelField = z.string().trim().min(1, LABEL_REQUIRED_MESSAGE);

const optionalText = z
  .string()
  .trim()
  .transform((value) => (value === "" ? null : value))
  .nullable();

// An unchecked box posts nothing at all, which `parseFormData` reads back as
// an empty string — so presence is the value, whatever the control's own.
const checkboxField = z.string().transform((value) => value !== "");

export const skillSchema = z.object({
  code: codeField,
  label: labelField,
  description: z
    .string(DESCRIPTION_REQUIRED_MESSAGE)
    .trim()
    .min(1, DESCRIPTION_REQUIRED_MESSAGE),
  icon: optionalText,
  color: optionalText.refine(
    (value) => value === null || HEX_COLOR_PATTERN.test(value),
    COLOR_MESSAGE,
  ),
});

// Codes are omitted rather than validated on edit: templates and achievements
// reference them by name, so they are fixed once the record exists.
export const skillUpdateSchema = skillSchema.omit({ code: true });

export const topicSchema = z.object({
  code: codeField,
  label: labelField,
  icon: optionalText,
  sortOrder: z
    .string()
    .trim()
    .transform((value) => (value === "" ? "0" : value))
    .refine((value) => Number.isInteger(Number(value)), SORT_ORDER_MESSAGE)
    .transform(Number),
});

export const topicUpdateSchema = topicSchema.omit({ code: true });

export const tagUpdateSchema = z.object({
  label: labelField,
  kind: z.enum(TAG_KINDS, KIND_MESSAGE),
});

// JSON request bodies: real types, and no `.transform()` anywhere, because a
// transform cannot be expressed in JSON Schema and would break the generated
// OpenAPI document. Route handlers and `src/lib/openapi.ts` use these.

// An absent optional field and an explicit null both mean "no value". Empty
// strings are rejected rather than folded into null: the form family only
// coerces them because a browser cannot send an absent field.
const EMPTY_STRING_MESSAGE = "Send null rather than an empty string.";

const jsonCode = z
  .string()
  .trim()
  .min(1, CODE_REQUIRED_MESSAGE)
  .regex(CODE_PATTERN, CODE_SHAPE_MESSAGE);

const jsonLabel = z.string().trim().min(1, LABEL_REQUIRED_MESSAGE);

const jsonOptionalText = z
  .string()
  .trim()
  .min(1, EMPTY_STRING_MESSAGE)
  .nullable()
  .default(null);

export const skillCreateBody = z.object({
  code: jsonCode,
  label: jsonLabel,
  description: z
    .string(DESCRIPTION_REQUIRED_MESSAGE)
    .trim()
    .min(1, DESCRIPTION_REQUIRED_MESSAGE),
  icon: jsonOptionalText,
  color: z
    .string()
    .trim()
    .regex(HEX_COLOR_PATTERN, COLOR_MESSAGE)
    .nullable()
    .default(null),
});

export const skillUpdateBody = skillCreateBody.omit({ code: true });

// The whole body is optional: archiving without stating a reason stays legal,
// so an absent body parses to the same value an explicit null does.
export const skillDeactivateBody = z.object({
  note: jsonOptionalText,
});

export const topicCreateBody = z.object({
  code: jsonCode,
  label: jsonLabel,
  icon: jsonOptionalText,
  // The message is set on the type as well as the check: a string reaching this
  // field fails as "not a number" before `.int()` ever runs.
  sortOrder: z.number(SORT_ORDER_MESSAGE).int(SORT_ORDER_MESSAGE).default(0),
});

export const topicUpdateBody = topicCreateBody.omit({ code: true });

export const tagUpdateBody = z.object({
  label: jsonLabel,
  kind: z.enum(TAG_KINDS, KIND_MESSAGE),
});

// The slug carries the same normalisation the editor applies on write, so a
// tag typed once during authoring still lands in the disciplined shape.
export const tagCreateBody = tagUpdateBody.extend({
  slug: z.string().trim().regex(TAG_SLUG_PATTERN, TAG_SLUG_MESSAGE),
});

// Merging names the survivor, and the tag in the path is the one that goes.
// Only tags can merge: nothing holds a foreign key to one, so the templates
// carrying the loser can simply be repointed.
export const tagMergeBody = z.object({
  survivorSlug: z
    .string()
    .trim()
    .regex(TAG_SLUG_PATTERN, TAG_SLUG_MESSAGE),
});

const GOAL_TYPE_MESSAGE = "Pick one of the listed goal types.";
const VERIFICATION_MESSAGE = "Pick one of the listed verification methods.";
const PARTICIPATION_MESSAGE = "Pick at least one participation mode.";
const SETTING_MESSAGE = "Pick one of the listed settings.";
const CONTENT_GRADE_MESSAGE = "Pick one of the listed content grades.";
const OPEN_SUBJECT_MESSAGE = "Open subject is true or false.";
const COOLDOWN_MESSAGE = "Cooldown must be a whole number of days above zero.";
const TITLE_REQUIRED_MESSAGE = "Title is required.";
// Both mirror a CHECK on template_translation.title: a title is a label, so it
// is capped at what a card can show and carries no placeholder to format.
const TITLE_LENGTH_MESSAGE = "Title must be 40 characters or fewer.";
const TITLE_PLACEHOLDER_MESSAGE =
  "A title is never formatted, so it cannot hold { or }. Placeholders live in the assignment.";
const ASSIGNMENT_REQUIRED_MESSAGE = "Assignment is required.";
const POSITIVE_ID_MESSAGE = "Must be the id of an existing record.";

export const TITLE_MAX_LENGTH = 40;

const NO_PLACEHOLDER_PATTERN = /^[^{}]*$/;

// Reused by the form and JSON families, which differ only in how they arrive.
const titleRules = <T extends z.ZodString>(field: T) =>
  field
    .trim()
    .min(1, TITLE_REQUIRED_MESSAGE)
    .max(TITLE_MAX_LENGTH, TITLE_LENGTH_MESSAGE)
    .regex(NO_PLACEHOLDER_PATTERN, TITLE_PLACEHOLDER_MESSAGE);

const jsonId = z
  .number(POSITIVE_ID_MESSAGE)
  .int(POSITIVE_ID_MESSAGE)
  .positive(POSITIVE_ID_MESSAGE);

const jsonNullableId = jsonId.nullable().default(null);

// Duplicates are not rejected here and not transformed away either — a
// transform cannot be expressed in JSON Schema, per the note above. Postgres
// cannot reject {SOLO,SOLO} in a CHECK either, since subqueries are forbidden
// there, so the query layer deduplicates on write. `&&` behaves identically
// either way; the normalising only keeps the stored array honest.
const participationField = z
  .array(z.enum(PARTICIPATIONS, PARTICIPATION_MESSAGE))
  .min(1, PARTICIPATION_MESSAGE);

const tagsField = z
  .array(z.string().trim().regex(TAG_SLUG_PATTERN, TAG_SLUG_MESSAGE))
  .default([]);

// The columns a template carries itself. Wording lives in
// template_translation and is written through the translation endpoints, with
// one exception: creating a template has to seed the default-locale row,
// because title and assignment_template are both NOT NULL and a template
// without them is not a template.
const templateFields = {
  // Nullable on purpose: it exists for the handful of rows app code names by
  // hand. Forcing it only ever produced QUEST_1 and ASDF.
  code: jsonOptionalText,
  goalType: z.enum(GOAL_TYPES, GOAL_TYPE_MESSAGE),
  metricId: jsonNullableId,
  verification: z.enum(VERIFICATIONS, VERIFICATION_MESSAGE),
  // Required before publish, not before saving: the draft exemption exists so
  // staff can save incomplete work.
  topicId: jsonNullableId,
  participation: participationField,
  setting: z.enum(SETTINGS, SETTING_MESSAGE),
  tags: tagsField,
  // Null means "once, ever". The default of 1 makes quests repeatable unless
  // an author says otherwise.
  cooldownDays: z
    .number(COOLDOWN_MESSAGE)
    .int(COOLDOWN_MESSAGE)
    .positive(COOLDOWN_MESSAGE)
    .nullable()
    .default(1),
  contentGrade: z.enum(CONTENT_GRADES, CONTENT_GRADE_MESSAGE).default("STOCK"),
  // False by default and deliberately narrow: true only where the wording
  // hands the subject choice to the user. Implied freedom — any book, any
  // dish — is not open, or every quest would be and the flag would say
  // nothing.
  hasOpenSubject: z.boolean(OPEN_SUBJECT_MESSAGE).default(false),
};

// Bindings may travel with the template so that one authoring form is one
// transaction: a template naming a skill archived mid-edit has to fail whole
// rather than land with half its bindings. They stay optional, so the
// dedicated /skills and /traits endpoints remain the way to change bindings
// alone, and an absent key means "leave them as they are".
const templateBindingFields = {
  skillIds: z.array(jsonId).optional(),
  requiresTraitIds: z.array(jsonId).optional(),
  grantsTraitIds: z.array(jsonId).optional(),
};

export const templateCreateBody = z.object({
  ...templateFields,
  ...templateBindingFields,
  title: titleRules(z.string(TITLE_REQUIRED_MESSAGE)),
  assignmentTemplate: z
    .string(ASSIGNMENT_REQUIRED_MESSAGE)
    .trim()
    .min(1, ASSIGNMENT_REQUIRED_MESSAGE),
});

// Wording is omitted on update: it is versioned per locale and replacing it
// here would silently overwrite the default locale without staling the others.
export const templateUpdateBody = z.object({
  ...templateFields,
  ...templateBindingFields,
});

// The assignment is ICU MessageFormat, not naive substitution. It is not
// parsed here: a malformed pattern is a publish-gate failure reported per
// locale alongside the placeholder rules, so that saving a draft mid-edit
// stays possible. The title carries no ICU at all, and its two rules are
// CHECKs — restated here so a bad one is a 422 rather than a 500.
export const translationWriteBody = z.object({
  title: titleRules(z.string(TITLE_REQUIRED_MESSAGE)),
  assignmentTemplate: z
    .string(ASSIGNMENT_REQUIRED_MESSAGE)
    .trim()
    .min(1, ASSIGNMENT_REQUIRED_MESSAGE),
});

// A set, replaced whole. Duplicates are deduplicated by the query layer for
// the same reason the classification arrays are.
export const templateSkillsBody = z.object({
  skillIds: z.array(jsonId).default([]),
});

const DIFFICULTY_MESSAGE = "Difficulty must be a whole number from 1 to 10.";
const EFFORT_MESSAGE = "Pick one of the listed effort bands.";
const TARGET_MESSAGE = "Target must be a number above zero.";
// Above one on purpose: a one-day repetition is a plain quest, and NULL is
// the only spelling of "no repetition" the repetition_shape CHECK accepts.
const REQUIRED_DAYS_MESSAGE =
  "Required days must be a whole number above one, or blank.";
const DURATION_MESSAGE = "Duration must be a whole number of days above zero.";
const TIMESTAMP_MESSAGE = "Use an ISO 8601 timestamp.";

const positiveDays = (message: string) =>
  z.number(message).int(message).positive(message).nullable().default(null);

const repeatedDays = (message: string) =>
  z.number(message).int(message).min(2, message).nullable().default(null);

// goal_type is deliberately absent: it is a denormalized copy of the
// template's, policed by a composite foreign key, so the query layer reads it
// from the template rather than letting a client send one that cannot match.
export const rungWriteBody = z.object({
  // numeric(12,3). Sent as a number rather than a string because that is what
  // JSON callers have; the query layer formats it for Postgres.
  targetValue: z
    .number(TARGET_MESSAGE)
    .positive(TARGET_MESSAGE)
    .nullable()
    .default(null),
  requiredDays: repeatedDays(REQUIRED_DAYS_MESSAGE),
  difficultyLevel: z
    .number(DIFFICULTY_MESSAGE)
    .int(DIFFICULTY_MESSAGE)
    .min(1, DIFFICULTY_MESSAGE)
    .max(10, DIFFICULTY_MESSAGE),
  effort: z.enum(EFFORTS, EFFORT_MESSAGE),
  // NULL by default: most quests never expire, and authors opt in to a
  // deadline rather than out of one.
  durationDays: positiveDays(DURATION_MESSAGE),
  activeFrom: z.iso
    .datetime({ offset: true, message: TIMESTAMP_MESSAGE })
    .nullable()
    .default(null),
  activeTo: z.iso
    .datetime({ offset: true, message: TIMESTAMP_MESSAGE })
    .nullable()
    .default(null),
});

const UNIT_REQUIRED_MESSAGE = "Unit is required.";
const AGG_MESSAGE = "Pick one of the listed aggregations.";

// Mirrors metric_agg_check. SUM is the only one a THRESHOLD template may bind,
// because progress is a running sum; the publish gate is what enforces that.
export const METRIC_AGGS = ["SUM", "MAX", "LAST"] as const;

// `source` is deliberately not a field: metric_check pins an author-created
// metric to USER_INPUT, so the handler writes it rather than accepting it.
export const metricCreateBody = z.object({
  code: jsonCode,
  unit: z.string(UNIT_REQUIRED_MESSAGE).trim().min(1, UNIT_REQUIRED_MESSAGE),
  agg: z.enum(METRIC_AGGS, AGG_MESSAGE),
});

export const metricUpdateBody = metricCreateBody.omit({ code: true });

const SELF_DECLARE_MESSAGE = "Send null rather than an empty question.";

export const traitCreateBody = z.object({
  code: jsonCode,
  label: jsonLabel,
  // Only meaningful on an onboarding question, but not conditioned on it: a
  // trait can be promoted to the survey later, and the wording written now
  // should survive that.
  selfDeclareQuestion: z
    .string()
    .trim()
    .min(1, SELF_DECLARE_MESSAGE)
    .nullable()
    .default(null),
  isOnboardingQuestion: z.boolean().default(false),
  sortOrder: z.number(SORT_ORDER_MESSAGE).int(SORT_ORDER_MESSAGE).default(0),
});

export const traitUpdateBody = traitCreateBody.omit({ code: true });

export const traitTranslationBody = traitCreateBody.pick({
  label: true,
  selfDeclareQuestion: true,
});

// Two sets in one body because they are one authoring decision: written
// separately, a template can sit momentarily requiring what it also grants.
export const templateTraitsBody = z.object({
  requiresTraitIds: z.array(jsonId).default([]),
  grantsTraitIds: z.array(jsonId).default([]),
});

// Form schemas for quest templates and rungs. Every field arrives from
// FormData as a string, so these coerce on the way in — the JSON bodies above
// take real types and share only the messages and the closed sets.

const OPTION_MESSAGE = "Pick one of the listed options.";

// A datetime-local input posts "2026-08-18T09:00" with no zone. Read as local
// time, which is what the person typing it meant.
const optionalDateField = z
  .string()
  .trim()
  .refine(
    (value) => value === "" || !Number.isNaN(Date.parse(value)),
    TIMESTAMP_MESSAGE,
  )
  .transform((value) => (value === "" ? null : new Date(value).toISOString()));

// A select's "none" option posts an empty string, which means null rather than
// a malformed id.
const optionalIdField = z
  .string()
  .trim()
  .refine(
    (value) => value === "" || /^[1-9][0-9]*$/.test(value),
    OPTION_MESSAGE,
  )
  .transform((value) => (value === "" ? null : Number(value)));

const optionalCountField = (message: string) =>
  z
    .string()
    .trim()
    .refine((value) => value === "" || /^[1-9][0-9]*$/.test(value), message)
    .transform((value) => (value === "" ? null : Number(value)));

// Ids reach a form as strings; anything that is not a positive integer never
// came from a control this app rendered, so it is dropped rather than reported.
const idListField = z
  .array(z.string())
  .default([])
  .transform((values) =>
    values.filter((value) => /^[1-9][0-9]*$/.test(value)).map(Number),
  );

const optionalRepeatedDaysField = (message: string) =>
  z
    .string()
    .trim()
    .refine((value) => value === "" || /^[1-9][0-9]*$/.test(value), message)
    .transform((value) => (value === "" ? null : Number(value)))
    .refine((value) => value === null || value > 1, message);

// Tags are typed as one comma-separated field rather than picked from a list:
// the vocabulary is a suggestion, and a tag typed once during authoring is
// still cheaper than a migration. The slug shape is enforced either way.
const tagsTextField = z
  .string()
  .trim()
  .transform((value) =>
    value
      .split(",")
      .map((tag) => tag.trim())
      .filter((tag) => tag !== ""),
  )
  .refine(
    (tags) => tags.every((tag) => TAG_SLUG_PATTERN.test(tag)),
    TAG_SLUG_MESSAGE,
  )
  .transform((tags) => [...new Set(tags)]);

export const templateFormSchema = z.object({
  code: optionalText,
  goalType: z.enum(GOAL_TYPES, GOAL_TYPE_MESSAGE),
  metricId: optionalIdField,
  verification: z.enum(VERIFICATIONS, VERIFICATION_MESSAGE),
  topicId: optionalIdField,
  participation: z
    .array(z.enum(PARTICIPATIONS, PARTICIPATION_MESSAGE))
    .min(1, PARTICIPATION_MESSAGE)
    .transform((values) => [...new Set(values)]),
  setting: z.enum(SETTINGS, SETTING_MESSAGE),
  tags: tagsTextField,
  cooldownDays: optionalCountField(COOLDOWN_MESSAGE),
  contentGrade: z.enum(CONTENT_GRADES, CONTENT_GRADE_MESSAGE),
  hasOpenSubject: checkboxField,
  title: titleRules(z.string()),
  assignment: z.string().trim().min(1, ASSIGNMENT_REQUIRED_MESSAGE),
  // Bound with the template rather than after it: the publish gate demands at
  // least one skill, so a create form that could not collect them would leave
  // every new template publish-blocked by construction.
  skillIds: idListField,
  requiresTraitIds: idListField,
  grantsTraitIds: idListField,
});

// The field names that post more than one value, so the action and the form
// agree on which controls to read with getAll.
export const TEMPLATE_MULTI_FIELDS = [
  "participation",
  "skillIds",
  "requiresTraitIds",
  "grantsTraitIds",
] as const;

// Wording is written through the translation endpoints once a template exists:
// it is versioned per locale, so writing it here would stale every translation
// on an edit that never touched the wording. The bindings have no such
// problem and ride along with the row.
export const templateEditFormSchema = templateFormSchema.omit({
  title: true,
  assignment: true,
});

export const templateWordingFormSchema = templateFormSchema.pick({
  title: true,
  assignment: true,
});

// goalType is absent: a rung copies it from its template, where a composite
// foreign key proves the copy matches.
export const rungFormSchema = z.object({
  targetValue: z
    .string()
    .trim()
    .refine((value) => value === "" || Number(value) > 0, TARGET_MESSAGE)
    .transform((value) => (value === "" ? null : Number(value))),
  requiredDays: optionalRepeatedDaysField(REQUIRED_DAYS_MESSAGE),
  difficultyLevel: z
    .string()
    .trim()
    .refine((value) => /^([1-9]|10)$/.test(value), DIFFICULTY_MESSAGE)
    .transform(Number),
  effort: z.enum(EFFORTS, EFFORT_MESSAGE),
  durationDays: optionalCountField(DURATION_MESSAGE),
  // Carried through the form so that saving a rung does not silently clear a
  // window someone set: PUT replaces every field.
  activeFrom: optionalDateField,
  activeTo: optionalDateField,
});

// Trait forms. A trait is one yes/no fact about a person: templates require it
// to be offerable and grant it on completion, and the signup survey can ask
// for it directly — which is why the question is authored rather than shipped
// as application i18n.
const traitFields = {
  label: labelField,
  // Only meaningful on an onboarding question, but not conditioned on it: a
  // trait can be promoted into the survey later, and wording written now
  // should survive that.
  selfDeclareQuestion: optionalText,
  // An unchecked checkbox posts nothing at all, which `parseFormData` reads
  // back as the empty string.
  isOnboardingQuestion: z.string().transform((value) => value !== ""),
  sortOrder: z
    .string()
    .trim()
    .transform((value) => (value === "" ? "0" : value))
    .refine((value) => Number.isInteger(Number(value)), SORT_ORDER_MESSAGE)
    .transform(Number),
};

export const traitSchema = z.object({ code: codeField, ...traitFields });

// The code is omitted on edit: templates gate on it and users hold it, so two
// spellings mean a gate that matches for some people and not others.
export const traitUpdateSchema = z.object(traitFields);
